import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Lifebuoy, MagnifyingGlass, ChatCircleDots, ArrowRight, Warning, CheckCircle, Clock,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
    { key: "open", label: "Open", cls: "bg-rose-100 text-rose-700" },
    { key: "in_progress", label: "In progress", cls: "bg-amber-100 text-amber-700" },
    { key: "resolved", label: "Resolved", cls: "bg-emerald-100 text-emerald-700" },
    { key: "closed", label: "Closed", cls: "bg-secondary text-muted-foreground" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.key, s]));

const PRIORITY_OPTIONS = [
    { key: "low", label: "Low", cls: "text-muted-foreground" },
    { key: "normal", label: "Normal", cls: "text-foreground" },
    { key: "high", label: "High", cls: "text-orange-600" },
    { key: "urgent", label: "Urgent", cls: "text-rose-600 font-bold" },
];
const PRIORITY_MAP = Object.fromEntries(PRIORITY_OPTIONS.map((p) => [p.key, p]));

export default function SupportTickets() {
    const [tickets, setTickets] = useState([]);
    const [q, setQ] = useState("");
    const [activeStatus, setActiveStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/support/tickets")
            .then((r) => setTickets(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        let list = tickets;
        if (activeStatus !== "all") list = list.filter((t) => t.status === activeStatus);
        if (q) {
            const l = q.toLowerCase();
            list = list.filter((t) =>
                (t.title || "").toLowerCase().includes(l) ||
                (t.description || "").toLowerCase().includes(l) ||
                (t.created_by_name || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [tickets, q, activeStatus]);

    const counts = useMemo(() => {
        const c = { all: tickets.length };
        STATUS_OPTIONS.forEach((s) => { c[s.key] = tickets.filter((t) => t.status === s.key).length; });
        return c;
    }, [tickets]);

    const openDetail = async (t) => {
        try {
            const r = await api.get(`/support/tickets/${t.id}`);
            setSelected(r.data);
        } catch {
            toast.error("Failed to open ticket");
        }
    };

    return (
        <div data-testid="support-tickets">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold">Tickets</h1>
                    <p className="text-muted-foreground mt-1 text-sm">{tickets.length} tickets, track, respond, resolve.</p>
                </div>
                <div className="relative w-full max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search title, author..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="support-search"
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="support-status-tabs">
                {[{ key: "all", label: "All" }, ...STATUS_OPTIONS].map((s) => (
                    <button
                        key={s.key}
                        onClick={() => setActiveStatus(s.key)}
                        data-testid={`support-tab-${s.key}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                            activeStatus === s.key ? "bg-primary text-white border-primary" : "bg-card text-foreground border-border hover:bg-secondary"
                        }`}
                    >
                        {s.label} ({counts[s.key] ?? 0})
                    </button>
                ))}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground">
                    <Lifebuoy size={32} className="mx-auto mb-2 opacity-50" />
                    No tickets here.
                </div>
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {filtered.map((t) => {
                    const status = STATUS_MAP[t.status] || STATUS_OPTIONS[0];
                    const prio = PRIORITY_MAP[t.priority] || PRIORITY_OPTIONS[1];
                    return (
                        <button
                            key={t.id}
                            onClick={() => openDetail(t)}
                            className="w-full text-left grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors items-center"
                            data-testid={`support-ticket-${t.id}`}
                        >
                            <div className="col-span-12 md:col-span-5 min-w-0">
                                <div className="font-medium text-sm truncate flex items-center gap-2">
                                    {t.priority === "urgent" && <Warning size={14} weight="fill" className="text-rose-600 shrink-0" />}
                                    <span className="truncate">{t.title}</span>
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {t.created_by_name || "Unknown"} ({t.created_by_role}) · {new Date(t.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                </div>
                            </div>
                            <div className={`col-span-6 md:col-span-2 text-xs ${prio.cls}`}>{prio.label}</div>
                            <div className="col-span-6 md:col-span-2">
                                <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold ${status.cls}`}>{status.label}</span>
                            </div>
                            <div className="col-span-12 md:col-span-3 text-xs text-muted-foreground flex items-center gap-2 md:justify-end">
                                <ChatCircleDots size={12} /> {(t.notes_log || []).length}
                                <ArrowRight size={14} />
                            </div>
                        </button>
                    );
                })}
            </div>

            <TicketDetailDialog
                ticket={selected}
                onClose={() => setSelected(null)}
                onChange={() => { load(); selected && openDetail(selected); }}
            />
        </div>
    );
}

function TicketDetailDialog({ ticket, onClose, onChange }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);

    const submitNote = async () => {
        if (!note.trim() || !ticket) return;
        setBusy(true);
        try {
            await api.post(`/support/tickets/${ticket.id}/note`, { text: note });
            setNote("");
            onChange();
            toast.success("Note added");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const changeStatus = async (status) => {
        if (!ticket) return;
        setStatusBusy(true);
        try {
            await api.put(`/support/tickets/${ticket.id}/status`, { status });
            toast.success(`Marked ${STATUS_MAP[status].label}`);
            onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setStatusBusy(false);
        }
    };

    if (!ticket) return null;
    const status = STATUS_MAP[ticket.status] || STATUS_OPTIONS[0];
    const prio = PRIORITY_MAP[ticket.priority] || PRIORITY_OPTIONS[1];
    const notes = [...(ticket.notes_log || [])].reverse();

    return (
        <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 flex-wrap">
                        {ticket.title}
                        <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold ${status.cls}`}>{status.label}</span>
                        <span className={`text-[10px] uppercase tracking-wider ${prio.cls}`}>{prio.label}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="text-xs text-muted-foreground mb-3">
                    Filed by {ticket.created_by_name || "Unknown"} ({ticket.created_by_role}) on {new Date(ticket.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="bg-secondary/40 rounded-xl p-4 text-sm whitespace-pre-wrap" data-testid="support-ticket-description">{ticket.description}</div>

                {/* Status actions */}
                <div className="flex flex-wrap gap-2 mt-4">
                    {STATUS_OPTIONS.map((s) => (
                        <Button
                            key={s.key}
                            size="sm"
                            variant={s.key === ticket.status ? "default" : "outline"}
                            disabled={statusBusy || s.key === ticket.status}
                            onClick={() => changeStatus(s.key)}
                            data-testid={`support-status-${s.key}`}
                        >
                            {s.key === "resolved" && <CheckCircle size={14} className="mr-1.5" />}
                            {s.key === "in_progress" && <Clock size={14} className="mr-1.5" />}
                            {s.label}
                        </Button>
                    ))}
                </div>

                {/* Note form */}
                <div className="mt-5">
                    <Label className="text-xs font-semibold">Add a note</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1" placeholder="What did you check? Who did you reach out to?" data-testid="support-note-textarea" />
                    <div className="flex justify-end mt-2">
                        <Button size="sm" onClick={submitNote} disabled={busy || !note.trim()} data-testid="support-note-save">
                            {busy ? "Saving…" : "Add note"}
                        </Button>
                    </div>
                </div>

                {notes.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-border space-y-3">
                        {notes.map((n) => (
                            <div key={n.id} className="flex gap-3" data-testid={`support-note-${n.id}`}>
                                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                                    {(n.author_name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground">{n.author_name}</span> · {new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                    <div className="text-sm mt-1 whitespace-pre-wrap">{n.text}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter className="mt-5">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
